import React, { useState, useMemo, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { AiOutlineCloudUpload, AiOutlineFileExcel, AiOutlineSetting, AiOutlineWarning, AiOutlineCheck, AiOutlineCopy, AiOutlineCloudDownload } from 'react-icons/ai';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MaxRefundModule = ({ annexureRows = [], rowData }) => {
    const [rateMap, setRateMap] = useState({});
    const [stats, setStats] = useState({ sheets: [], rows: 0, invertedTurnover: 0, invertedTax: 0 });
    const [loading, setLoading] = useState(false);
    const [gstr1Uploaded, setGstr1Uploaded] = useState(false);
    const [valA, setValA] = useState("");
    const [valB, setValB] = useState("");
    const [valC, setValC] = useState("");
    const [valD, setValD] = useState("");
    const [valE, setValE] = useState("");
    const fileInputRef = useRef(null);

    // ── localStorage key scoped to this client ──────────────────────────────
    const lsKey = `max_refund_${rowData?.client?.id || rowData?.id || 'default'}`;

    // ── Load saved data on mount ─────────────────────────────
    useEffect(() => {
        try {
            const saved = localStorage.getItem(lsKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.rateMap) setRateMap(parsed.rateMap);
                if (parsed.stats) setStats(parsed.stats);
                if (parsed.gstr1Uploaded) setGstr1Uploaded(parsed.gstr1Uploaded);
                if (parsed.valA !== undefined) setValA(parsed.valA);
                if (parsed.valB !== undefined) setValB(parsed.valB);
                if (parsed.valC !== undefined) setValC(parsed.valC);
                if (parsed.valD !== undefined) setValD(parsed.valD);
                if (parsed.valE !== undefined) setValE(parsed.valE);
            }
        } catch (err) {
            console.warn('MaxRefund: failed to load from localStorage', err);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lsKey]);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(lsKey);
            let parsed = saved ? JSON.parse(saved) : {};
            parsed = { ...parsed, valA, valB, valC, valD, valE };
            localStorage.setItem(lsKey, JSON.stringify(parsed));
        } catch (err) {
            console.warn('MaxRefund: failed to save to localStorage', err);
        }
    }, [valA, valB, valC, valD, valE, lsKey]);

    // Global Helpers
    function safeNum(v) {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
    }

    function roundTwo(n) {
        return Math.round(n * 100) / 100;
    }

    function findSheet(wb, names) {
        for (const name of names) {
            const found = wb.SheetNames.find(
                s => s.trim().toLowerCase() === name.trim().toLowerCase()
            );
            if (found) return wb.Sheets[found];
        }
        return null;
    }

    function formatINR(v) {
        const n = parseFloat(v);
        if (isNaN(n) || n === 0) return "—";
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(n);
    }

    const handleGSTR1Upload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);

        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer);

        const newRateMap = {};
        const sheetsFound = [];
        let totalRows = 0;

        const processSheet = (ws, startColOffset, sign, sheetName) => {
            if (!ws) return;
            sheetsFound.push(sheetName);
            const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            const dataRows = rows.slice(5);
            totalRows += dataRows.length;

            dataRows.forEach(row => {
                const rateRaw = row[startColOffset + 0];
                const rateNum = parseFloat(rateRaw);
                const rateKey = isNaN(rateNum) ? "0" : roundTwo(rateNum).toString();

                if (!newRateMap[rateKey]) {
                    newRateMap[rateKey] = { taxable: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
                }

                newRateMap[rateKey].taxable += sign * safeNum(row[startColOffset + 1]);
                newRateMap[rateKey].igst += sign * safeNum(row[startColOffset + 2]);
                newRateMap[rateKey].cgst += sign * safeNum(row[startColOffset + 3]);
                newRateMap[rateKey].sgst += sign * safeNum(row[startColOffset + 4]);
                newRateMap[rateKey].cess += sign * safeNum(row[startColOffset + 5]);
            });
        };

        processSheet(findSheet(wb, ['GSTR-1-1A_B2B', 'GSTR-1_B2B', 'B2B']), 7, 1, 'B2B');
        processSheet(findSheet(wb, ['GSTR-1-1A_B2CL', 'GSTR-1_B2CL', 'B2CL']), 7, 1, 'B2CL');
        processSheet(findSheet(wb, ['GSTR-1-1A_B2CS', 'GSTR-1_B2CS', 'B2CS']), 4, 1, 'B2CS');
        processSheet(findSheet(wb, ['GSTR-1-1A_CDN', 'GSTR-1_CDN', 'CDN']), 11, -1, 'CDN');

        let invertedTurnover = 0;
        let invertedTax = 0;
        const sortedRates = Object.entries(newRateMap)
            .sort(([a], [b]) => parseFloat(a) - parseFloat(b));

        let selected = [];
        const rateCount = sortedRates.length;

        if (rateCount === 1) {
            selected = [sortedRates[0]];
        } else if (rateCount === 2) {
            // If 2 rates, pick lower rate only
            selected = [sortedRates[0]];
        } else if (rateCount > 2) {
            // If more than 2, pick lowest 2 rates
            selected = [sortedRates[0], sortedRates[1]];
        }

        selected.forEach(([rk, d]) => {
            invertedTurnover += d.taxable;
            invertedTax += (d.igst + d.cgst + d.sgst);
        });

        setRateMap(newRateMap);
        setStats({
            sheets: sheetsFound,
            rows: totalRows,
            invertedTurnover,
            invertedTax
        });
        setGstr1Uploaded(true);
        setLoading(false);

        // ── Save to localStorage ────────────────────────────────
        try {
            const saved = localStorage.getItem(lsKey);
            let parsed = saved ? JSON.parse(saved) : {};
            parsed = {
                ...parsed,
                rateMap: newRateMap,
                stats: { sheets: sheetsFound, rows: totalRows, invertedTurnover, invertedTax },
                gstr1Uploaded: true
            };
            localStorage.setItem(lsKey, JSON.stringify(parsed));
        } catch (err) {
            console.warn('MaxRefund: failed to save to localStorage', err);
        }
    };

    const annexData = useMemo(() => {
        const sums = {
            "Inputs": { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, itc: 0 },
            "Input Services": { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, itc: 0 },
            "Capital Goods": { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, itc: 0 },
            "GRAND TOTAL": { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, itc: 0 }
        };

        annexureRows.forEach(row => {
            if (!row || !Array.isArray(row)) return;
            
            // category is at index 9 (Col 10)
            const category = String(row[9] || "Inputs");
            
            // Values from Annexure B already have correct sign
            // (CDNR credit notes are already negative from AnnexureBModule)
            const taxable = safeNum(row[8]);
            // cgst is at index 11 (Col 12), sgst 12 (Col 13), igst 13 (Col 14), cess 14 (Col 15)
            const cgst = safeNum(row[11]); 
            const sgst = safeNum(row[12]); 
            const igst = safeNum(row[13]); 
            const cess = safeNum(row[14]); 
            const itc = cgst + sgst + igst + cess; 

            let targetGroup = "Inputs";
            if (category.toLowerCase().includes("services")) targetGroup = "Input Services";
            else if (category.toLowerCase().includes("capital")) targetGroup = "Capital Goods";

            sums[targetGroup].taxable += taxable;
            sums[targetGroup].cgst += cgst;
            sums[targetGroup].sgst += sgst;
            sums[targetGroup].igst += igst;
            sums[targetGroup].cess += cess;
            sums[targetGroup].itc += itc;

            sums["GRAND TOTAL"].taxable += taxable;
            sums["GRAND TOTAL"].cgst += cgst;
            sums["GRAND TOTAL"].sgst += sgst;
            sums["GRAND TOTAL"].igst += igst;
            sums["GRAND TOTAL"].cess += cess;
            sums["GRAND TOTAL"].itc += itc;
        });

        const annexTotalRows = [
            { label: "TOTAL INPUT", ...sums["Inputs"] },
            { label: "TOTAL INPUT SERVICES", ...sums["Input Services"] },
            { label: "TOTAL CAPITAL GOODS", ...sums["Capital Goods"] },
            { label: "GRAND TOTAL", ...sums["GRAND TOTAL"] }
        ];

        return { 
            totalInputTax_M: sums["Inputs"].itc, 
            totalInputServicesTax_M: sums["Input Services"].itc, 
            totalCapital_M: sums["Capital Goods"].itc, 
            grandTotal_M: sums["GRAND TOTAL"].itc, 
            annexTotalRows 
        };
    }, [annexureRows]);

    const summary = useMemo(() => {
        const invertedTurnover = safeNum(valA);
        const totalTurnover = safeNum(valB);
        const totalInputTax_M = safeNum(valC);
        const taxOnInverted = safeNum(valD);
        const totalInputAndServices = safeNum(valE);

        const d46 = totalTurnover === 0 ? 0 : roundTwo((invertedTurnover / totalTurnover) * totalInputTax_M);
        const d48 = totalInputAndServices === 0 ? 0 : roundTwo((taxOnInverted / totalInputAndServices) * totalInputTax_M);
        const maxRefund = roundTwo(d46 - d48);

        return { invertedTurnover, totalTurnover, totalInputTax_M, taxOnInverted, totalInputAndServices, d46, d48, maxRefund };
    }, [valA, valB, valC, valD, valE]);

    const downloadExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Max Refund');

        // Styles
        const sectionHeaderStyle = { font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } }, alignment: { horizontal: 'center' } };
        const labelStyle = { font: { bold: true }, border: { bottom: { style: 'thin' } } };
        const borderStyle = { border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } };
        const refundHighlightStyle = { font: { bold: true, size: 14, color: { argb: 'FF92400E' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFBEB' } }, border: { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } } };

        // 1. Title
        sheet.addRow(["MAXIMUM REFUND CALCULATION REPORT"]);
        sheet.mergeCells(1, 1, 1, 6);
        sheet.getRow(1).getCell(1).style = sectionHeaderStyle;
        sheet.getRow(1).height = 30;

        // 2. Rate-wise Aggregation (GSTR-1)
        sheet.addRow([]);
        sheet.addRow(["A. RATE-WISE AGGREGATION (GSTR-1)"]).getCell(1).style = { font: { bold: true, size: 11 } };
        const rateHeaders = ["Rate %", "Taxable Value", "IGST", "CGST", "SGST", "CESS", "Total Tax"];
        const rHeaderRow = sheet.addRow(rateHeaders);
        rHeaderRow.eachCell(cell => { cell.style = { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }, border: borderStyle.border } });

        const sortedRates = Object.entries(rateMap).sort(([a], [b]) => parseFloat(a) - parseFloat(b));
        sortedRates.forEach(([rate, d]) => {
            const row = sheet.addRow([parseFloat(rate) + "%", d.taxable, d.igst, d.cgst, d.sgst, d.cess, d.igst + d.cgst + d.sgst]);
            row.eachCell(cell => { cell.border = borderStyle.border; });
        });

        // 3. Annexure B Totals
        sheet.addRow([]);
        sheet.addRow(["B. ANNEXURE B TOTALS"]).getCell(1).style = { font: { bold: true, size: 11 } };
        const annexHeaders = ["Annexure Row", "Taxable Value", "IGST", "CGST", "SGST", "CESS", "Total Tax (ITC)"];
        const aHeaderRow = sheet.addRow(annexHeaders);
        aHeaderRow.eachCell(cell => { cell.style = { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }, border: borderStyle.border } });

        annexData.annexTotalRows.forEach(r => {
            const row = sheet.addRow([r.label, r.taxable, r.igst, r.cgst, r.sgst, r.cess, r.itc]);
            if (r.label === "GRAND TOTAL") {
                row.eachCell(cell => { cell.font = { bold: true }; cell.border = borderStyle.border; });
            } else {
                row.eachCell(cell => { cell.border = borderStyle.border; });
            }
        });

        // 4. Refund Summary Calculation
        sheet.addRow([]);
        sheet.addRow(["C. MAXIMUM REFUND CALCULATION SUMMARY"]).getCell(1).style = { font: { bold: true, size: 11 } };
        
        const summaryData = [
            ["A - Turnover of inverted rated supply of goods", summary.invertedTurnover],
            ["B - Adjusted Total Turnover", summary.totalTurnover],
            ["C - Net ITC (Inputs)", summary.totalInputTax_M],
            ["D - Tax Payable on inverted rated supply", summary.taxOnInverted],
            ["E - ITC on inputs and input services", summary.totalInputAndServices],
            [],
            ["Maximum Refund Amount (A/B * C) - (D/E * C)", ""],
            ["Step 1: (A/B * C)", summary.d46],
            ["Step 2: (D/E * C)", summary.d48],
            ["MAXIMUM REFUND", summary.maxRefund]
        ];

        summaryData.forEach((rowArr, i) => {
            const row = sheet.addRow(rowArr);
            if (rowArr.length > 0) {
                row.getCell(1).font = { bold: i === summaryData.length - 1 };
                if (i === summaryData.length - 1) {
                    row.getCell(1).style = refundHighlightStyle;
                    row.getCell(2).style = refundHighlightStyle;
                }
            }
        });

        // Formatting
        sheet.columns = [
            { width: 45 }, { width: 18 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }
        ];

        sheet.eachRow((row) => {
            row.eachCell((cell, colIdx) => {
                if (colIdx > 1 && typeof cell.value === 'number') {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "Max_Refund_Calculation.xlsx");
    };

    return (
        <div className="max-refund-module">
            <style>{`
                .max-refund-module { background: #ffffff; color: #1e293b; padding: 24px; border-radius: 12px; font-family: 'DM Mono', monospace; }
                .syne { font-family: 'Syne', sans-serif; font-weight: 800; }
                .error-box { background: rgba(251, 113, 133, 0.08); border: 1px solid #fb7185; color: #be123c; padding: 16px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px; }
                .upload-zone { border: 2px dashed #cbd5e1; border-radius: 12px; padding: 32px; text-align: center; cursor: pointer; margin-bottom: 24px; transition: all 0.3s; }
                .upload-zone:hover { border-color: #d97706; background: rgba(245, 158, 11, 0.05); }
                .stats-panel { background: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; border-radius: 8px; margin-bottom: 24px; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
                .stat-item label { color: #64748b; font-size: 11px; display: block; margin-bottom: 4px; }
                .stat-item span { color: #d97706; font-weight: bold; }
                .table-container { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; margin-bottom: 24px; }
                table { width: 100%; border-collapse: separate; border-spacing: 0; }
                th { background: #f1f5f9; color: #475569; padding: 12px; font-size: 12px; text-align: left; border-bottom: 2px solid #e2e8f0; }
                td { padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #e2e8f0; color: #334155; }
                .inverted-row { background: rgba(45, 212, 191, 0.06) !important; }
                .badge-teal { background: #2dd4bf; color: #134e4a; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; margin-left: 8px; }
                .summary-block { background: #f8fafc; border: 1px solid #e2e8f0; padding: 32px; border-radius: 12px; position: relative; }
                .summary-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
                .summary-row:last-child { border-bottom: none; margin-top: 16px; padding-top: 16px; border-top: 2px solid #cbd5e1; }
                .label-red { color: #dc2626; font-weight: 500; }
                .label-blue { color: #2563eb; font-weight: 500; }
                .val-primary { font-size: 16px; color: #1e293b; font-weight: bold; }
                .max-refund-val { font-size: 28px; color: #d97706; font-weight: 900; }
                .divider { height: 1px; background: #e2e8f0; margin: 16px 0; }
                .btn-xlsx { background: #d97706; color: #ffffff; border: none; padding: 12px 24px; border-radius: 8px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: 0.3s; margin-top: 24px; }
                .btn-xlsx:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(217, 119, 6, 0.3); }
            `}</style>

            <h2 className="syne" style={{ color: '#d97706', marginBottom: '24px' }}>Maximum Refund Calculator</h2>

            {annexureRows?.length < 1 && (
                <div className="error-box"><AiOutlineWarning size={20} /> Annexure B is not built yet. Some calculations will be zero.</div>
            )}

            {!gstr1Uploaded ? (
                <div className="upload-zone" onClick={() => fileInputRef.current.click()}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleGSTR1Upload} />
                    <AiOutlineCloudUpload size={48} color="#d97706" style={{ marginBottom: '12px' }} />
                    <h4 className="syne">Upload GSTR-1 Excel File</h4>
                    <p style={{ color: '#64748b' }}>Reads rate-wise data from B2B, B2CL, B2CS, and CDN sheets</p>
                </div>
            ) : (
                <>
                    <div className="stats-panel">
                        <div className="stat-item"><label>Sheets Found</label><span>{stats.sheets.join(', ')}</span></div>
                        <div className="stat-item"><label>Rows Processed</label><span>{stats.rows}</span></div>
                        <div className="stat-item"><label>Inverted Turnover</label><span>₹{formatINR(stats.invertedTurnover)}</span></div>
                        <div className="stat-item"><label>Inverted Tax</label><span>₹{formatINR(stats.invertedTax)}</span></div>
                    </div>
                    <div className="table-container">
                        <h5 className="syne" style={{ padding: '16px', margin: 0, background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#1e293b' }}>Annexure B Totals</h5>
                        <table>
                            <thead>
                                <tr>
                                    <th>Annexure Row</th><th>Taxable Value</th><th>IGST</th><th>CGST</th><th>SGST</th><th>CESS</th><th>Total Tax (ITC)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {annexData.annexTotalRows.map((r, i) => (
                                    <tr key={i} style={r.label === 'GRAND TOTAL' ? { fontWeight: 'bold', background: '#f8fafc' } : {}}>
                                        <td style={{ fontWeight: 'bold', color: r.label === 'GRAND TOTAL' ? '#1e293b' : '#d97706' }}>{r.label}</td>
                                        <td>{formatINR(r.taxable)}</td>
                                        <td>{formatINR(r.igst)}</td>
                                        <td>{formatINR(r.cgst)}</td>
                                        <td>{formatINR(r.sgst)}</td>
                                        <td>{formatINR(r.cess)}</td>
                                        <td>{formatINR(r.itc)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-container">
                        <h5 className="syne" style={{ padding: '16px', margin: 0, background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#1e293b' }}>
                            Rate-wise Aggregation (GSTR-1)
                            <button className="btn btn-sm btn-outline-secondary float-right" style={{marginTop:'-5px'}} onClick={() => { setGstr1Uploaded(false); localStorage.removeItem(lsKey); }}>Reset</button>
                        </h5>
                        <table>
                            <thead>
                                <tr>
                                    <th>Rate %</th><th>Taxable</th><th>IGST</th><th>CGST</th><th>SGST</th><th>CESS</th><th>Total Tax</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(rateMap).sort(([a], [b]) => parseFloat(a) - parseFloat(b)).map(([rate, d]) => (
                                    <tr key={rate}>
                                        <td style={{ fontWeight: 'bold' }}>{parseFloat(rate)}%</td>
                                        <td>{formatINR(d.taxable)}</td>
                                        <td>{formatINR(d.igst)}</td>
                                        <td>{formatINR(d.cgst)}</td>
                                        <td>{formatINR(d.sgst)}</td>
                                        <td>{formatINR(d.cess)}</td>
                                        <td style={{ fontWeight: 'bold', color: '#d97706' }}>{formatINR(d.igst + d.cgst + d.sgst)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="summary-block">
                        <div className="summary-row">
                            <span className="label-red">A - Turnover of inverted rated supply</span>
                            <div className="d-flex align-items-center">
                                <span style={{ marginRight: '8px', color: '#64748b' }}>₹</span>
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm" 
                                    style={{ width: '150px', fontWeight: 'bold' }} 
                                    value={valA}
                                    onChange={(e) => setValA(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="summary-row">
                            <span className="label-red">B - Adjusted Total Turnover</span>
                            <div className="d-flex align-items-center">
                                <span style={{ marginRight: '8px', color: '#64748b' }}>₹</span>
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm" 
                                    style={{ width: '150px', fontWeight: 'bold' }} 
                                    value={valB}
                                    onChange={(e) => setValB(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="summary-row">
                            <span className="label-red">C - Net ITC (Inputs)</span>
                            <div className="d-flex align-items-center">
                                <span style={{ marginRight: '8px', color: '#64748b' }}>₹</span>
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm" 
                                    style={{ width: '150px', fontWeight: 'bold' }} 
                                    value={valC}
                                    onChange={(e) => setValC(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="summary-row">
                            <span className="label-red">D - Tax Payable on inverted rated supply</span>
                            <div className="d-flex align-items-center">
                                <span style={{ marginRight: '8px', color: '#64748b' }}>₹</span>
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm" 
                                    style={{ width: '150px', fontWeight: 'bold' }} 
                                    value={valD}
                                    onChange={(e) => setValD(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="summary-row">
                            <span className="label-red">E - ITC on inputs and input services</span>
                            <div className="d-flex align-items-center">
                                <span style={{ marginRight: '8px', color: '#64748b' }}>₹</span>
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm" 
                                    style={{ width: '150px', fontWeight: 'bold' }} 
                                    value={valE}
                                    onChange={(e) => setValE(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="summary-row"><span className="label-blue">A/B × C</span><span className="val-primary">₹{formatINR(summary.d46)}</span></div>
                        <div className="summary-row"><span className="label-blue">D/E × C</span><span className="val-primary">₹{formatINR(summary.d48)}</span></div>
                        <div className="summary-row">
                            <span className="syne" style={{ fontSize: '20px', color: '#d97706' }}>★ MAXIMUM REFUND</span>
                            <span className="max-refund-val">₹{formatINR(summary.maxRefund)}</span>
                        </div>
                    </div>




                    <button className="btn-xlsx" onClick={downloadExcel}>
                        <AiOutlineCloudDownload size={20} /> Download MaxRefund_Calculation.xlsx
                    </button>
                </>
            )}

            {loading && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                    <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#d97706', borderRadius: '50%', animation: 'spin-max 1s linear infinite' }}></div>
                    <style>{`@keyframes spin-max { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
        </div>
    );
};

export default MaxRefundModule;
